<!-- src/views/VenueDashboard.vue -->

<template>
  <div class="container coredeck-page">
    <div class="section" style="padding-bottom: 0">
      <div class="row" style="margin-bottom: 0">
        <div class="col s12">
          <h4 style="margin: 0 0 10px 0">Dashboard</h4>
        </div>
      </div>

      <RotaToolbar
        :weekRangeText="weekRangeText"
        :weekEnding="viewWeekEnding"
        :canGoNext="canGoNext"
        :busy="loading || pettyLoading || printing"
        :showPrintWeeklyPack="true"
        @prev="goPrevWeek"
        @next="goNextWeek"
        @print-weekly-pack="onPrintWeeklyPack"
      />
    </div>

    <VenueKpiRow :items="kpiItems" />

    <div class="row">
      <div class="col s12">
        <RevenueComparisonChartCard />
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watch, ref, onMounted, onUnmounted } from "vue";

import { useRoute, useRouter } from "vue-router";

import { percent0dp, moneyGBP } from "@/scripts/sharedHelpers/formatter";

import { auth, db } from "@/firebase";

import { doc, getDoc } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

/* global M */

import RotaToolbar from "@/components/rota/RotaToolbar.vue";
import VenueKpiRow from "@/components/dashboard/VenueKpiRow.vue";
import RevenueComparisonChartCard from "@/components/dashboard/RevenueComparisonChartCard.vue";
import SafeWeekSummaryCard from "@/components/dashboard/SafeWeekSummaryCard.vue";

import { useAuditKpis } from "@/scripts/useScripts/useAuditKpis";

import { usePettyCashApi } from "@/scripts/useScripts/usePettyCashApi";

import {
  useVenueContext,
  setViewWeekEndingOverride,
  clearViewWeekEndingOverride,
} from "@/scripts/useScripts/useVenueContext";

const route = useRoute();
const router = useRouter();

const { venueId, weekEnding } = useVenueContext();

function toISODate(d) {
  const yyyy = d.getFullYear();

  const mm = String(d.getMonth() + 1).padStart(2, "0");

  const dd = String(d.getDate()).padStart(2, "0");

  return `${yyyy}-${mm}-${dd}`;
}

function parseISO(iso) {
  const [y, m, d] = String(iso)
    .split("-")
    .map((x) => Number(x));

  return new Date(y, (m || 1) - 1, d || 1, 12, 0, 0, 0);
}

function addDays(d, n) {
  const x = new Date(d);

  x.setDate(x.getDate() + n);

  return x;
}

function addWeeks(d, n) {
  return addDays(d, n * 7);
}

function weekEndingSundayForDate(d) {
  const day = d.getDay();

  const delta = (7 - day) % 7;

  return addDays(d, delta);
}

const thisWeekEnding = computed(() => toISODate(weekEndingSundayForDate(new Date())));

const viewWeekEnding = ref("");

function resolveInitialViewWeek() {
  const q = route.query.w;

  if (typeof q === "string" && /^\d{4}-\d{2}-\d{2}$/.test(q)) {
    return q;
  }

  const ctx = weekEnding?.value;

  if (typeof ctx === "string" && /^\d{4}-\d{2}-\d{2}$/.test(ctx)) {
    return ctx;
  }

  return thisWeekEnding.value;
}

async function setViewWeek(nextISO) {
  viewWeekEnding.value = nextISO;

  if (route.query.w !== nextISO) {
    await router.replace({
      query: {
        ...route.query,
        w: nextISO,
      },
    });
  }

  setViewWeekEndingOverride(nextISO);
}

function formatUK(d) {
  const day = String(d.getDate()).padStart(2, "0");

  const month = d.toLocaleString("en-GB", {
    month: "short",
  });

  const year = d.getFullYear();

  return `${day} ${month} ${year}`;
}

const weekRangeText = computed(() => {
  if (!viewWeekEnding.value) {
    return "";
  }

  const end = parseISO(viewWeekEnding.value);

  const start = addDays(end, -6);

  return `${formatUK(start)} – ${formatUK(end)}`;
});

const maxWeekEnding = computed(() =>
  toISODate(addWeeks(parseISO(thisWeekEnding.value), 10))
);

const canGoNext = computed(() => {
  if (!viewWeekEnding.value) {
    return false;
  }

  const next = addWeeks(parseISO(viewWeekEnding.value), 1);

  return toISODate(next) <= maxWeekEnding.value;
});

async function goPrevWeek() {
  if (!viewWeekEnding.value) {
    return;
  }

  const prev = addWeeks(parseISO(viewWeekEnding.value), -1);

  await setViewWeek(toISODate(prev));
}

async function goNextWeek() {
  if (!canGoNext.value) {
    return;
  }

  const next = addWeeks(parseISO(viewWeekEnding.value), 1);

  await setViewWeek(toISODate(next));
}

const isOperations = ref(false);

async function resolveOperationsAccess() {
  const user = auth.currentUser;

  if (!user) {
    return;
  }

  const snap = await getDoc(doc(db, "users", user.uid));

  const d = snap.exists() ? snap.data() : {};

  isOperations.value =
    d.isOperations === true || d.is_operations === true || d.operations === true;
}

const printing = ref(false);

const printError = ref("");

function fns() {
  return getFunctions(undefined, "us-central1");
}

function extractUrl(data) {
  return (
    data?.downloadUrl ||
    data?.url ||
    data?.weeklyPackUrl ||
    data?.pdfUrl ||
    data?.signedUrl ||
    ""
  );
}

function displayDate(value) {
  const clean = String(value || "")
    .trim()
    .slice(0, 10);

  if (!/^\d{4}-\d{2}-\d{2}$/.test(clean)) {
    return clean;
  }

  const [yyyy, mm, dd] = clean.split("-");

  return `${dd}/${mm}/${yyyy}`;
}

async function onPrintWeeklyPack(payload = {}) {
  if (printing.value) {
    return;
  }

  printError.value = "";

  const selectedVenueId = String(venueId.value || "").trim();

  const selectedWeekEnding = String(
    payload?.weekEnding || viewWeekEnding.value || weekEnding.value || ""
  )
    .trim()
    .slice(0, 10);

  if (!selectedVenueId) {
    printError.value = "No venue selected.";

    M?.toast?.({
      html: printError.value,
    });

    return;
  }

  if (!/^\d{4}-\d{2}-\d{2}$/.test(selectedWeekEnding)) {
    printError.value = "No valid week ending selected.";

    M?.toast?.({
      html: printError.value,
    });

    return;
  }

  printing.value = true;

  try {
    const fn = httpsCallable(fns(), "generateWeeklyPack");

    const res = await fn({
      venueId: selectedVenueId,

      displayedWeekEnding: selectedWeekEnding,

      weekEnding: selectedWeekEnding,

      forceRegenerate: true,

      requestedAt: new Date().toISOString(),
    });

    const url = extractUrl(res?.data);

    if (!url) {
      throw new Error("Weekly pack generated but no PDF URL returned.");
    }

    window.open(url, "_blank", "noopener,noreferrer");

    M?.toast?.({
      html: `Weekly pack generated for W/E ${displayDate(
        res?.data?.reportWeekEnding || selectedWeekEnding
      )}.`,
    });
  } catch (error) {
    console.error("[VenueDashboard] generateWeeklyPack failed:", error);

    printError.value = error?.message || String(error);

    M?.toast?.({
      html: printError.value,
    });
  } finally {
    printing.value = false;
  }
}

onMounted(async () => {
  await resolveOperationsAccess();

  if (typeof M !== "undefined") {
    const els = document.querySelectorAll(".collapsible");

    if (els.length) {
      M.Collapsible.init(els);
    }
  }

  await setViewWeek(resolveInitialViewWeek());
});

watch(
  () => route.query.w,

  async (w) => {
    if (
      typeof w === "string" &&
      /^\d{4}-\d{2}-\d{2}$/.test(w) &&
      w !== viewWeekEnding.value
    ) {
      await setViewWeek(w);
    }
  }
);

onUnmounted(() => {
  clearViewWeekEndingOverride();
});

const {
  loading,
  error,
  adminAvgWTD,
  venueAvgWTD,
  hasAdminAuditWTD,
  hasVenueAuditWTD,
  loadAuditWeekToDate,
} = useAuditKpis({
  weekEnding,
});

const {
  loading: pettyLoading,

  error: pettyError,

  pettyCashTotal,
  actualRevenueTotal,
  pctOfRevenue,
} = usePettyCashApi();

let inFlight = false;

watch(
  [venueId, weekEnding],

  async ([v, we]) => {
    console.log("[DASH] venueId/weekEnding watch:", v, we);

    if (!v || !we) {
      return;
    }

    if (inFlight) {
      console.log("[DASH] KPI load skipped (inFlight).");

      return;
    }

    inFlight = true;

    try {
      console.log(
        "[DASH] Calling loadAuditWeekToDate with venueId:",
        v,
        "weekEnding:",
        we
      );

      await loadAuditWeekToDate(v);
    } finally {
      inFlight = false;
    }
  },

  {
    immediate: true,
  }
);

const kpiItems = computed(() => [
  {
    key: "adminAvgWTD",

    label: "Admin Audit Score (WTD)",

    value: adminAvgWTD.value,

    delta: null,

    formatter: percent0dp,

    tone: "admin-audit",

    hasScore: hasAdminAuditWTD.value,
  },

  {
    key: "venueAvgWTD",

    label: "Venue Audit Score (WTD)",

    value: venueAvgWTD.value,

    delta: null,

    formatter: percent0dp,

    tone: "venue-audit",

    hasScore: hasVenueAuditWTD.value,
  },

  {
    key: "pettyCashTotal",

    label: "Petty Cash Total (Week)",

    value: pettyCashTotal.value,

    delta: null,

    subLabel:
      actualRevenueTotal.value > 0
        ? `${percent0dp(pctOfRevenue.value)} of Actual Revenue (${moneyGBP(
            actualRevenueTotal.value
          )})`
        : `0% of Actual Revenue (${moneyGBP(actualRevenueTotal.value)})`,

    formatter: moneyGBP,
  },
]);

watch(
  venueId,

  (v) => console.log("[DASH] venueId changed:", v),

  {
    immediate: true,
  }
);

watch(
  weekEnding,

  (v) => console.log("[DASH] effective weekEnding changed:", v),

  {
    immediate: true,
  }
);

watch(
  error,

  (v) => v && console.warn("[DASH] useAuditKpis error:", v),

  {
    immediate: true,
  }
);

watch(
  pettyError,

  (v) => v && console.warn("[DASH] usePettyCashApi error:", v),

  {
    immediate: true,
  }
);
</script>
